import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import {
  HomeIcon,
  CubeIcon,
  PuzzlePieceIcon,
  BookmarkIcon,
  Cog6ToothIcon,
  ArchiveBoxIcon,
  SparklesIcon,
  DocumentTextIcon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  CodeBracketIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import useVersion from '../hooks/useVersion'
import { useAuth } from '../contexts/AuthContext'

const navigation = [
  { name: '仪表板', href: '/dashboard', icon: HomeIcon },
  { name: '小部件配置', href: '/widgets', icon: PuzzlePieceIcon },
  { name: '服务配置', href: '/services', icon: CubeIcon },
  { name: '书签配置', href: '/bookmarks', icon: BookmarkIcon },
  { name: '基础设置', href: '/settings', icon: Cog6ToothIcon },
  { name: '配置模板', href: '/templates', icon: SparklesIcon },
  { name: '自定义CSS', href: '/custom-styles', icon: CodeBracketIcon },
  { name: '官方配置文档', href: '/docs', icon: DocumentTextIcon },
  { name: '更新日志', href: '/changelog', icon: ClockIcon },
  { name: '备份管理', href: '/backups', icon: ArchiveBoxIcon },
]

const Sidebar = ({ open, onClose }) => {
  const location = useLocation()
  const version = useVersion()
  const { isPasswordProtected, logout } = useAuth()

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 bg-gray-900/95 backdrop-blur-xl border-r border-gray-700/50 transform transition-all duration-500 ease-in-out shadow-2xl',
          // 移动端：根据 open 状态显示/隐藏
          open ? 'translate-x-0' : '-translate-x-full',
          // 大屏幕：始终显示
          'lg:translate-x-0'
        )}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-700/50 bg-gradient-to-r from-blue-600/10 to-purple-600/10">
          <div className="flex flex-col">
            <h1 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
              🏠 配置管理器
            </h1>
            <span className="text-xs text-gray-400 mt-0.5">
              v{version}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors lg:hidden"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <nav className="mt-6 px-3">
          <div className="space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={onClose}
                  className={clsx(
                    'nav-link group',
                    isActive
                      ? 'nav-link-active'
                      : 'nav-link-inactive'
                  )}
                >
                  <item.icon className="nav-icon" />
                  {item.name}
                </NavLink>
              )
            })}
          </div>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700">
          {/* 登出按钮 - 仅在密码保护模式下显示 */}
          {isPasswordProtected && (
            <button
              onClick={logout}
              className="w-full flex items-center justify-center px-3 py-2 mb-3 text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
              登出
            </button>
          )}

          <div className="text-xs text-gray-400 text-center">
            Homepage Config Manager
          </div>
        </div>
      </div>
    </>
  )
}

export default Sidebar
